import React, { Fragment } from 'react';
import styles from './HighlightedText.module.scss';
import { withStyles } from '../../../utils/utils';

const cn = withStyles(styles);

export type HighlightedTextProps = {
  text: string;
  searchTerm: string;
  className?: string;
};

type ParsedSegment = {
  text: string;
  isMatch: boolean;
};

const parseTextSegments = (text: string, searchTerm: string): ParsedSegment[] => {
  // Try using Intl.Segmenter with grapheme granularity

  try {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

    const textGrapheme = Array.from(segmenter.segment(text), (s) => s.segment);
    const termGrapheme = Array.from(segmenter.segment(searchTerm.toLowerCase()), (s) => s.segment);

    const termLower = termGrapheme.join('');

    const result: ParsedSegment[] = [];
    let buffer = '';

    let i = 0;
    while (i < textGrapheme.length) {
      const textWindow = textGrapheme.slice(i, i + termGrapheme.length).join('');

      if (textWindow.toLowerCase() === termLower) {
        if (buffer) {
          result.push({ text: buffer, isMatch: false });
          buffer = '';
        }
        result.push({ text: textWindow, isMatch: true });
        i += termGrapheme.length;
      } else {
        buffer += textGrapheme[i];
        i++;
      }
    }

    if (buffer) result.push({ text: buffer, isMatch: false });
    return result;
  } catch (error) {
    const parts = text.split(new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));

    return parts.map((part) => ({
      text: part,
      isMatch: part.toLowerCase() === searchTerm.toLowerCase(),
    }));
  }

  // Fallback to regex approach
};

export const HighlightedText: React.FC<HighlightedTextProps> = ({ text, searchTerm, className }) => {
  if (!searchTerm || !text) {
    return <>{text}</>;
  }

  const segments = parseTextSegments(text, searchTerm);

  return (
    <>
      {segments.map((segment, index) =>
        segment.isMatch ? (
          <mark key={`${index}-${segment.text}`} className={cn('highlight', className)}>
            {segment.text}
          </mark>
        ) : (
          <Fragment key={`${index}-${segment.text}`}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
};

export default HighlightedText;
